/**
 * AdminFinancialPlanTable - Tabela de receita agrupada por plano
 * Exibe nome do plano, preço unitário, quantidade de tenants, subtotal e % do MRR
 * Filtro Todos/Ativos para alternar entre dados de todos os tenants ou somente ativos
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import type { PlanRevenue } from '@/hooks/useAdminFinancial';

/** Formata valores monetários em BRL */
const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

interface Props {
  planRevenues: PlanRevenue[];
  mrrPlans: number;
  /** Receita por plano incluindo todos os tenants (active + trialing + past_due) */
  allPlanRevenues: PlanRevenue[];
  /** MRR total de planos incluindo todos os tenants */
  mrrPlansAll: number;
}

export function AdminFinancialPlanTable({ planRevenues, mrrPlans, allPlanRevenues, mrrPlansAll }: Props) {
  /** Filtro ativo: 'ativos' mostra somente pagantes, 'todos' inclui past_due */
  const [filter, setFilter] = useState<'ativos' | 'todos'>('ativos');

  /** Dados exibidos conforme filtro selecionado */
  const data = filter === 'ativos' ? planRevenues : allPlanRevenues;
  const totalMrr = filter === 'ativos' ? mrrPlans : mrrPlansAll;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Receita por Plano</CardTitle>
          {/* Toggle de filtro Todos / Ativos */}
          <div className="flex gap-1">
            <Button
              variant={filter === 'todos' ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setFilter('todos')}
            >
              Todos
            </Button>
            <Button
              variant={filter === 'ativos' ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setFilter('ativos')}
            >
              Ativos
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plano</TableHead>
              <TableHead className="text-right">Preço Unit.</TableHead>
              <TableHead className="text-center">Tenants</TableHead>
              <TableHead className="text-right">Subtotal</TableHead>
              <TableHead className="text-right">% MRR</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((plan) => (
              <TableRow key={plan.plan_name}>
                <TableCell className="font-medium">{plan.plan_name}</TableCell>
                <TableCell className="text-right">{formatBRL(plan.price_monthly)}</TableCell>
                <TableCell className="text-center">{plan.tenant_count}</TableCell>
                <TableCell className="text-right font-medium">{formatBRL(plan.subtotal)}</TableCell>
                <TableCell className="text-right text-muted-foreground">{plan.percentage.toFixed(1)}%</TableCell>
              </TableRow>
            ))}
            {/* Linha de total */}
            <TableRow className="bg-muted/50 font-bold">
              <TableCell>Total</TableCell>
              <TableCell />
              <TableCell className="text-center">
                {data.reduce((s, p) => s + p.tenant_count, 0)}
              </TableCell>
              <TableCell className="text-right">{formatBRL(totalMrr)}</TableCell>
              <TableCell className="text-right">100%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
